import React, { useState, useEffect } from "react";
import { FaSearch, FaSpinner, FaChevronDown, FaPlus, FaMinus } from "react-icons/fa";
import api from "@/services/api";

import { useComponentTranslation } from "@/services/i18n";

import useStore from "@/services/store";

function FilterExperiences({ filters, setFilters }) {
  const { organization } = useStore();
  const { t } = useComponentTranslation({
    en: { label: "Job Title", search_placeholder: "Search job title..." },
    fr: { label: "Intitulé de poste", search_placeholder: "Rechercher intitulé..." },
  });
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchOptions();
    }
  }, [isOpen, filters.countries, filters.cities, filters.skills, filters.universities, filters.languages]);

  async function fetchOptions() {
    try {
      setLoading(true);
      const { ok, data } = await api.post("/user/aggregate", {
        organization_id: organization._id,
        role_candidate: true,
        key: "experiences",
        countries: filters.countries,
        cities: filters.cities,
        skills: filters.skills,
        universities: filters.universities,
        languages: filters.languages,
      });
      if (ok && data) {
        const optionsArray = Object.entries(data)
          .map(([value, count]) => ({ value, count }))
          .sort((a, b) => b.count - a.count);
        setOptions(optionsArray);
      }
    } catch (error) {
      console.error("Error fetching job titles:", error);
    } finally {
      setLoading(false);
    }
  }

  function handleFilterChange(value, mode) {
    setFilters((prev) => {
      const updated = { ...prev };
      const currentList = updated.job_titles[mode];
      const otherMode = mode === "include" ? "exclude" : "include";
      const otherList = updated.job_titles[otherMode];
      updated.job_titles[otherMode] = otherList.filter((item) => item !== value);
      const index = currentList.indexOf(value);
      if (index > -1) {
        updated.job_titles[mode] = currentList.filter((item) => item !== value);
      } else {
        updated.job_titles[mode] = [...currentList, value];
      }
      return updated;
    });
  }

  const filteredOptions = options.filter((option) => option.value.toLowerCase().includes(search.toLowerCase()));
  const activeCount = filters.job_titles.include.length + filters.job_titles.exclude.length;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm text-gray-700 hover:text-gray-900 transition-colors"
      >
        <span>{t("label") || "Job Title"}</span>
        {activeCount > 0 && (
          <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded-full font-medium">
            {filters.job_titles.include.length > 0 && `+${filters.job_titles.include.length}`}
            {filters.job_titles.exclude.length > 0 && ` -${filters.job_titles.exclude.length}`}
          </span>
        )}
        <FaChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)}></div>
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg p-3 shadow-lg z-20 w-[320px]">
            <div className="relative mb-2">
              <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                placeholder={t("search_placeholder") || "Search..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>
            <div className="space-y-0.5 max-h-64 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <FaSpinner className="animate-spin text-gray-400" />
                </div>
              ) : filteredOptions.length === 0 ? (
                <p className="text-xs text-gray-500 py-2 text-center">No results found</p>
              ) : (
                filteredOptions.map((option) => {
                  const { value, count } = option;
                  const included = filters.job_titles.include.includes(value);
                  const excluded = filters.job_titles.exclude.includes(value);
                  return (
                    <div key={value} className="flex items-center gap-1 group hover:bg-gray-50 rounded p-1 -mx-1">
                      <button
                        onClick={() => handleFilterChange(value, "include")}
                        className={`p-1.5 rounded transition-colors ${
                          included ? "bg-green-100 text-green-700" : "text-gray-400 hover:text-green-600 hover:bg-green-50"
                        }`}
                        title="Include"
                      >
                        <FaPlus className="w-2.5 h-2.5" />
                      </button>
                      <button
                        onClick={() => handleFilterChange(value, "exclude")}
                        className={`p-1.5 rounded transition-colors ${
                          excluded ? "bg-red-100 text-red-700" : "text-gray-400 hover:text-red-600 hover:bg-red-50"
                        }`}
                        title="Exclude"
                      >
                        <FaMinus className="w-2.5 h-2.5" />
                      </button>
                      <span
                        className={`text-sm flex-1 ${
                          included ? "text-green-700 font-medium" : excluded ? "text-red-700 font-medium" : "text-gray-700"
                        } transition-colors flex items-center justify-between gap-2 px-1`}
                      >
                        <span className="truncate">{value || "Unknown"}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0 bg-gray-100 px-1.5 py-0.5 rounded-full">{count}</span>
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default FilterExperiences;

